import { useState } from 'react'
import { useTranslation, Trans } from 'react-i18next'

import { ConfirmationResultModal, Alert, Spacer, Text } from '@percent/lemonade'
import { useMutation } from '@percent/utility'
import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import type { DeleteGroupModalProps } from './DeleteGroupModal.types'

type ModalState = 'confirm' | 'success' | 'failure'

export function DeleteGroupModal({ isOpen, onClose, groupId, groupName, onSuccess }: DeleteGroupModalProps) {
  const { t } = useTranslation()
  const { groupService } = useServices()
  const [modalState, setModalState] = useState<ModalState>('confirm')

  const [{ isPending }, { mutateAsync: deleteGroup }] = useMutation(groupService.deleteGroup)

  const handleDelete = async () => {
    try {
      await deleteGroup({ id: groupId })
      setModalState('success')
    } catch {
      setModalState('failure')
    }
  }

  const handleClose = () => {
    if (!isPending) {
      setModalState('confirm')
      onClose()
    }
  }

  const handleSuccessClose = () => {
    setModalState('confirm')
    onSuccess?.()
    onClose()
  }

  const handleFailureClose = () => {
    setModalState('confirm')
    onClose()
  }

  if (!isOpen) {
    return null
  }

  return (
    <ConfirmationResultModal
      isOpen={isOpen}
      state={modalState === 'confirm' ? undefined : modalState}
      title={t('modal.deleteGroup.title')}
      primaryButtonText={isPending ? t('modal.deleteGroup.button.deleting') : t('button.delete')}
      secondaryButtonText={t('button.cancel')}
      onConfirm={handleDelete}
      onClose={modalState === 'success' ? handleSuccessClose : handleClose}
      onFailureClose={handleFailureClose}
      loading={isPending}
      primaryBtnTestId="delete-group-submit-button"
      secondaryBtnTestId="delete-group-cancel-button"
      success={{
        title: t('modal.deleteGroup.success.title'),
        description: (
          <Trans
            i18nKey="modal.deleteGroup.success.description"
            values={{ name: groupName }}
            components={{ bold: <Text as="span" fontWeight="semiBold" /> }}
          />
        ),
        button: t('button.close')
      }}
      failure={{
        title: t('modal.deleteGroup.error.title'),
        description: t('modal.deleteGroup.error.description'),
        button: t('button.close')
      }}
    >
      <Alert variant="warning" title={t('modal.deleteGroup.alert')} />
      <Spacer size={4} axis="vertical" />
      <Trans
        i18nKey="modal.deleteGroup.description"
        values={{ name: groupName }}
        components={{ bold: <Text as="span" fontWeight="semiBold" /> }}
      />
    </ConfirmationResultModal>
  )
}
